import React, { useCallback, useEffect, useState } from 'react';
import ClientContextLine from '../components/ClientContextLine';
import { useData } from '../contexts/DataContext';
import { adminApi } from '../services/api';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

// Tailwind classes per activity category — keeps the feed scannable.
const CATEGORY_STYLES = {
  auth: 'bg-blue-100 text-blue-800',
  order: 'bg-green-100 text-green-800',
  payment: 'bg-purple-100 text-purple-800',
  account: 'bg-gray-100 text-gray-800',
  shopping: 'bg-yellow-100 text-yellow-800',
  admin: 'bg-red-100 text-red-800',
  other: 'bg-gray-100 text-gray-700',
};

const prettyType = (t) =>
  (t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const RANGE_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last year', days: 365 },
];

const ActivityLog = () => {
  const { getAuditLogs } = useData();

  const [rows, setRows] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ eventType: '', q: '', days: 90 });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Load available event-type filters once.
  useEffect(() => {
    adminApi.getAuditLogEventTypes().then(setEventTypes).catch(() => setEventTypes([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = new Date(Date.now() - filters.days * 24 * 60 * 60 * 1000).toISOString();
      const params = { from, page, pageSize: 50 };
      if (filters.eventType) params.eventType = filters.eventType;
      if (filters.q) params.q = filters.q;
      const res = await getAuditLogs(params);
      setRows(res?.data || []);
      setHasMore(Boolean(res?.hasMore));
    } catch (e) {
      setError('Failed to load activity. Please try again.');
      setRows([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [getAuditLogs, filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setFilters((f) => ({ ...f, q: search.trim() }));
  };

  const onFilterChange = (patch) => {
    setPage(1);
    setFilters((f) => ({ ...f, ...patch }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <ClipboardDocumentListIcon className="h-7 w-7 text-indigo-600 mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          <ArrowPathIcon className={`-ml-1 mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-md p-4 flex flex-col md:flex-row md:items-end gap-4">
        <form onSubmit={applySearch} className="flex-1">
          <label className="block text-sm font-medium text-gray-500 mb-1 whitespace-nowrap">Search customer</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Email or name, then press Enter"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </form>

        <div className="w-full md:w-auto">
          <label className="block text-sm font-medium text-gray-500 mb-1 whitespace-nowrap">Event type</label>
          <select
            className="block w-full py-2 pl-3 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            value={filters.eventType}
            onChange={(e) => onFilterChange({ eventType: e.target.value })}
          >
            <option value="">All events</option>
            {eventTypes.map((t) => (
              <option key={t} value={t}>{prettyType(t)}</option>
            ))}
          </select>
        </div>

        <div className="w-full md:w-auto">
          <label className="block text-sm font-medium text-gray-500 mb-1 whitespace-nowrap">Time range</label>
          <select
            className="block w-full py-2 pl-3 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            value={filters.days}
            onChange={(e) => onFilterChange({ days: parseInt(e.target.value, 10) })}
          >
            {RANGE_OPTIONS.map((r) => (
              <option key={r.days} value={r.days}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">{error}</div>
      )}

      {/* Feed */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">No activity in this period.</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {rows.map((r, i) => (
              <li key={`${r.event_type}-${r.occurred_at}-${i}`} className="px-4 py-3 hover:bg-gray-50">
                <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_STYLES[r.event_category] || CATEGORY_STYLES.other}`}>
                        {prettyType(r.event_type)}
                      </span>
                      <span className="text-sm text-gray-900 break-words md:truncate">{r.description}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500 break-words md:truncate">
                      {r.user_name ? `${r.user_name} · ` : ''}{r.user_email || 'Unknown customer'}
                    </div>
                    <ClientContextLine row={r} />
                  </div>
                  <time className="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap md:ml-4">
                    {r.occurred_at ? new Date(r.occurred_at).toLocaleString() : ''}
                  </time>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">Page {page}</span>
        <div className="space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3 py-1 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore || loading}
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3 py-1 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
